import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface OrgAlertProps {
  /** The variant determines the color scheme: warning (yellow) or error (red) */
  variant: 'warning' | 'error';
  /** The title displayed in the alert header */
  title: string;
  /** The content to display in the alert body */
  children: React.ReactNode;
  /** Optional additional className for the Alert */
  className?: string;
}

const variantStyles = {
  warning: {
    alert: 'border-warning background-warning',
    text: 'text-warning',
  },
  error: {
    alert: 'border-error background-error',
    text: 'text-error',
  },
};

/**
 * A reusable organization alert component with warning and error variants.
 * Used for displaying important notifications like incomplete profiles or payment issues.
 */
export function OrgAlert({ variant, title, children, className = '' }: OrgAlertProps) {
  const styles = variantStyles[variant];

  return (
    <Alert
      variant="default"
      className={`${styles.alert} min-w-[200px] flex-shrink flex flex-col ${className}`}
    >
      <AlertTitle className={`flex gap-2 items-center text-sm font-semibold ${styles.text}`}>
        <AlertCircle className={`h-4 w-4 ${styles.text}`} />
        {title}
      </AlertTitle>
      <AlertDescription className={`w-full space-y-2 flex-1 flex flex-col ${styles.text}`}>
        {children}
      </AlertDescription>
    </Alert>
  );
}
